import { Plus, X } from 'lucide-react'
import { useCallback, useMemo, useState } from 'react'
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { Badge } from '../../shared/badge'
import type { ApproachStrategy } from '../types'

interface StrategySearchInputProps {
  /** Currently selected subtopics */
  selectedStrategies: { id: string; name: string }[]
  /** All available subtopic options to search from */
  allStrategies: ApproachStrategy[]
  /** Callback when selected subtopics change */
  onChange: (strategies: { id: string; name: string }[]) => void
  /** Optional placeholder text */
  placeholder?: string
  /** Optional className for the container */
  className?: string
}

export function StrategySearchInput({
  selectedStrategies,
  allStrategies,
  onChange,
  placeholder = 'Add subtopic...',
  className,
}: StrategySearchInputProps) {
  const [open, setOpen] = useState(false)
  const [searchValue, setSearchValue] = useState('')

  const selectedIds = useMemo(
    () => new Set(selectedStrategies.map((s) => s.id)),
    [selectedStrategies],
  )

  // Filter out selected/non-matching options, then group by topic.
  // Options arrive ordered by topic displayOrder, so insertion order is kept.
  const groupedOptions = useMemo(() => {
    const query = searchValue.trim().toLowerCase()
    const available = allStrategies.filter(
      (s) =>
        !selectedIds.has(s.id) &&
        (!query || s.name.toLowerCase().includes(query)),
    )
    const groups = new Map<string, ApproachStrategy[]>()
    for (const option of available) {
      const topicName = option.topicName || 'Other'
      if (!groups.has(topicName)) {
        groups.set(topicName, [])
      }
      groups.get(topicName)!.push(option)
    }
    return Array.from(groups.entries())
  }, [allStrategies, selectedIds, searchValue])

  const handleSelect = useCallback(
    (strategy: ApproachStrategy) => {
      onChange([
        ...selectedStrategies,
        { id: strategy.id, name: strategy.name },
      ])
      setSearchValue('')
      setOpen(false)
    },
    [selectedStrategies, onChange],
  )

  const handleRemove = useCallback(
    (strategyId: string) => {
      onChange(selectedStrategies.filter((s) => s.id !== strategyId))
    },
    [selectedStrategies, onChange],
  )

  return (
    <div className={cn('flex flex-wrap items-center gap-1.5', className)}>
      {/* Selected strategy badges */}
      {selectedStrategies.map((strategy) => (
        <Badge
          key={strategy.id}
          variant="topic"
          className="flex items-center gap-1 pr-1"
        >
          {strategy.name}
          <button
            type="button"
            onClick={() => handleRemove(strategy.id)}
            className="hover:bg-primary/20 ml-0.5 rounded p-0.5 transition-colors"
            aria-label={`Remove ${strategy.name}`}
          >
            <X className="h-3 w-3" />
          </button>
        </Badge>
      ))}

      {/* Search input popover */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            className={cn(
              'border-input hover:bg-muted flex h-7 items-center gap-1 rounded-md border border-dashed px-2 text-xs transition-colors',
              'text-muted-foreground hover:text-foreground',
            )}
          >
            <Plus className="h-3 w-3" />
            <span>{placeholder}</span>
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-64 p-0" align="start">
          <Command shouldFilter={false}>
            <CommandInput
              placeholder="Search subtopics..."
              value={searchValue}
              onValueChange={setSearchValue}
            />
            <CommandList>
              <CommandEmpty>
                {allStrategies.length === 0
                  ? 'No subtopics available'
                  : 'No matching subtopics'}
              </CommandEmpty>
              {groupedOptions.map(([topicName, options]) => (
                <CommandGroup key={topicName} heading={topicName}>
                  {options.map((subtopic) => (
                    <CommandItem
                      key={subtopic.id}
                      value={subtopic.id}
                      onSelect={() => handleSelect(subtopic)}
                      className="cursor-pointer"
                    >
                      <span className="flex-1">{subtopic.name}</span>
                    </CommandItem>
                  ))}
                </CommandGroup>
              ))}
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>
    </div>
  )
}
